import api from './api';
import {
  MeetingFormData,
  CreateMeetingResponse,
  MeetingsListResponse,
  Meeting,
  NoteDetail,
  MeetingApiResponse,
  NoteApiResponse,
} from '@/types';

export interface MeetingDetailResponse {
  meeting: Meeting & { notes: NoteDetail[] };
}

// Helper function to convert backend API response to frontend format
const convertMeetingFromApi = (apiMeeting: MeetingApiResponse): Meeting & { notes: NoteDetail[] } => {
  return {
    meeting_id: apiMeeting.id,
    user_id: apiMeeting.userId,
    person_name: apiMeeting.personName,
    company_name: apiMeeting.companyName,
    meeting_date: apiMeeting.meetingDate,
    meeting_time: apiMeeting.meetingTime,
    location: apiMeeting.location,
    created_at: apiMeeting.createdAt,
    user: apiMeeting.user,
    notes: (apiMeeting.notes || []).map(convertNoteFromApi),
  };
};

// Helper function to convert note API response to frontend format
const convertNoteFromApi = (apiNote: NoteApiResponse): NoteDetail => {
  // Convert backend enum values to frontend lowercase format
  const convertNoteType = (backendType: string): 'business_card' | 'handwritten' | 'text' | 'voice' => {
    const typeMap: Record<string, 'business_card' | 'handwritten' | 'text' | 'voice'> = {
      'BUSINESS_CARD': 'business_card',
      'HANDWRITTEN': 'handwritten',
      'TEXT': 'text',
      'VOICE': 'voice',
    };
    return typeMap[backendType] || 'text';
  };

  const convertStatus = (backendStatus: string): 'pending' | 'processing' | 'completed' | 'failed' => {
    const statusMap: Record<string, 'pending' | 'processing' | 'completed' | 'failed'> = {
      'PENDING': 'pending',
      'PROCESSING': 'processing',
      'COMPLETED': 'completed',
      'FAILED': 'failed',
    };
    return statusMap[backendStatus] || 'pending';
  };

  return {
    note_id: apiNote.id,
    note_type: convertNoteType(apiNote.noteType),
    processing_status: convertStatus(apiNote.processingStatus),
    created_at: apiNote.createdAt,
    content: apiNote.processedContent || apiNote.rawContent,
    file_path: apiNote.attachmentUrl,
    transcription: apiNote.processedContent || apiNote.rawContent, // Voice notes: processedContent is cleaned transcription
    business_card: apiNote.businessCard ? {
      full_name: apiNote.businessCard.personName,
      company: apiNote.businessCard.companyName,
      position: apiNote.businessCard.position,
      email: apiNote.businessCard.email,
      phoneMobile: apiNote.businessCard.phoneMobile,
      phoneLandline: apiNote.businessCard.phoneLandline,
      street: apiNote.businessCard.street,
      city: apiNote.businessCard.city,
      postalCode: apiNote.businessCard.postalCode,
      country: apiNote.businessCard.country,
      website: apiNote.businessCard.website,
    } : undefined,
  };
};

export const meetingsApi = {
  /**
   * Create new meeting via Express API
   */
  createMeeting: async (
    meetingData: MeetingFormData
  ): Promise<CreateMeetingResponse> => {
    const { data } = await api.post<CreateMeetingResponse>(
      '/meetings',
      meetingData
    );
    return data;
  },

  /**
   * Get meetings list via Express API
   */
  getMeetings: async (params?: {
    limit?: number;
    offset?: number;
    date_from?: string;
    date_to?: string;
    user_id?: number;
    company_name?: string;
    location?: string;
    note_types?: string; // Comma-separated string
  }): Promise<MeetingsListResponse> => {
    const { data } = await api.get<{ success: boolean; meetings: MeetingApiResponse[]; pagination: any }>('/meetings', {
      params
    });
    // Convert API response to frontend format
    return {
      success: data.success,
      meetings: data.meetings.map(convertMeetingFromApi),
      total: data.pagination?.total || 0,
      limit: data.pagination?.limit || params?.limit || 10,
      offset: data.pagination?.page ? (data.pagination.page - 1) * (params?.limit || 10) : (params?.offset || 0),
    };
  },

  /**
   * Get single meeting by ID with all notes details
   */
  getMeetingById: async (meetingId: number): Promise<MeetingDetailResponse> => {
    console.log('getMeetingById called with:', meetingId);
    try {
      const { data } = await api.get<{ success: boolean; meeting: MeetingApiResponse }>(`/meetings/${meetingId}`);
      console.log('getMeetingById response:', data);
      const converted = convertMeetingFromApi(data.meeting);
      console.log('getMeetingById converted:', converted);
      return {
        meeting: converted,
      };
    } catch (error) {
      console.error('getMeetingById error:', error);
      throw error;
    }
  },

  /**
   * Update meeting information
   */
  updateMeeting: async (
    meetingId: number,
    updateData: Partial<MeetingFormData>
  ): Promise<MeetingDetailResponse> => {
    // Convert snake_case to camelCase for backend
    const backendData: any = {};
    if (updateData.person_name !== undefined) backendData.personName = updateData.person_name;
    if (updateData.company_name !== undefined) backendData.companyName = updateData.company_name;
    if (updateData.meeting_date) backendData.meetingDate = updateData.meeting_date;
    if (updateData.meeting_time) backendData.meetingTime = updateData.meeting_time;
    if (updateData.location) backendData.location = updateData.location;

    const { data } = await api.put<{ success: boolean; meeting: MeetingApiResponse }>(
      `/meetings/${meetingId}`,
      backendData
    );
    return {
      meeting: convertMeetingFromApi(data.meeting),
    };
  },

  /**
   * Delete meeting and all associated notes
   */
  deleteMeeting: async (meetingId: number): Promise<{ message: string }> => {
    const { data } = await api.delete<{ message: string }>(`/meetings/${meetingId}`);
    return data;
  },

  /**
   * Get current user's meeting statistics
   */
  getUserStats: async (): Promise<{
    success: boolean;
    stats: {
      totalMeetings: number;
      notesByType: Record<string, { count: number; percentage: number }>;
      recentMeetings: any[];
    };
  }> => {
    const { data } = await api.get('/meetings/stats');
    return data;
  },
};
